// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import XDiv from "./XDiv";

import "./_style/scrollfadeout.css";

/**
 * @typedef {import("./XDiv").xdivparams} xdivparams
 * 
 * @typedef {Object} xscrollparamsAddOns
 * @property {boolean} [masked]
 * @property {boolean} [overflowScroll] - If true, enables overflow scrolling.
 * @property {boolean} [horizontal] - If true, scrolls horizontally instead of vertically.
 * @property {function} [onEndReached] - Callback function when the end is reached.
 * @property {number} [onEndReachedThreshold] - Threshold in pixels to trigger onEndReached before reaching the end.
 * @property {(callbacks: {
 *  scrollToBottom: () => void,
 *  scrollToTop: () => void,
 *  scrollToIndex: (index: number) => void
 * }) => void} [onMounted] - Callback function when the component is mounted, providing scrollToBottom method.
 * @property {React.RefObject} [forwardRef] - Forwarded ref to access the scrollable div.
 * @property {React.RefObject} [ctrls] - Ref object to expose scroll control methods.
 * 
 * @typedef {xdivparams & xscrollparamsAddOns} xscrollparams
 *
 * @param {xscrollparams} param0
 */
function XScroll({
  children,
  masked = false,
  overflowScroll = true,
  horizontal = false,
  onEndReached,
  onEndReachedThreshold = 100,
  onMounted,
  forwardRef,
  ctrls,
  ...others
}){

  const ref = useRef(null);
  const [endReached, setEndReached] = useState(false);

  const isCloseToBottom = useCallback((ref) => {
    if(ref.current){
      if(horizontal){
        return ref.current.scrollLeft + ref.current.clientWidth >= ref.current.scrollWidth - onEndReachedThreshold;
      }else{
        return ref.current.scrollTop + ref.current.clientHeight >= ref.current.scrollHeight - onEndReachedThreshold;
      }
    }
    return false;
  }, [horizontal, onEndReachedThreshold]);

  const _onScroll = useCallback((e) => {
    let isClose = isCloseToBottom(ref);
    if(!endReached && isClose){
      setEndReached(true);
      onEndReached?.(e);
    }else if(endReached && !isClose){
      setEndReached(false);
    }
  }, [ref, endReached, isCloseToBottom, onEndReached]);

  const scrollToTop = useCallback((smooth = true) => {
    if(ref.current){
      ref.current.scrollTo({
        top: 0,
        behavior: smooth ? "smooth" : "auto"
      });
    }
  }, [ref]);

  const scrollToBottom = useCallback((smooth = true) => {
    if(ref.current){
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto"
      });
    }
  }, [ref]);

  const scrollToIndex = useCallback((index, smooth = true) => {
    if(ref.current){
      const child = ref.current.children[index];
      if(child){
        child.scrollIntoView({behavior: smooth ? "smooth" : "auto"});
      }
    }
  }, [ref]);

  // Merge internal ref with external forwardRef prop
  const setRefs = useCallback((node) => {
    ref.current = node; // always keep internal working
    if(forwardRef){
      if(typeof forwardRef === "function"){
        forwardRef(node);
      }else{
        forwardRef.current = node;
      }
    }
  }, [forwardRef]);

  useImperativeHandle(ctrls, () => ({
    scrollToBottom: () => scrollToBottom(),
    scrollToTop: () => scrollToTop(),
    scrollToIndex: (index) => scrollToIndex(index)
  }), [scrollToBottom, scrollToTop, scrollToIndex]);

  useEffect(() => {
    if(ref.current && onMounted){
      let callbacks = {
        scrollToBottom: () => scrollToBottom(),
        scrollToTop: () => scrollToTop(),
        scrollToIndex: (index) => scrollToIndex(index)
      };
      onMounted(callbacks);
    }
    if (ref.current && overflowScroll) {
      const currentRef = ref.current;
      const updateMask = () => {
        if(!currentRef) return;
        const atTop = currentRef.scrollTop <= 0;
        const atBottom = currentRef.scrollTop + currentRef.clientHeight >= currentRef.scrollHeight - 1;
        const topVal = atTop ? 0 : "var(--mask-height)";
        const bottomVal = atBottom ? 0 : "var(--mask-height)";
        currentRef.style.setProperty("--mask-top", topVal);
        currentRef.style.setProperty("--mask-bottom", bottomVal);
      };
      updateMask();
      currentRef.addEventListener("scroll", _onScroll);
      currentRef.addEventListener("scroll", updateMask);
      window.addEventListener("resize", updateMask);
      return () => { 
        currentRef.removeEventListener("scroll", _onScroll);
        currentRef.removeEventListener("scroll", updateMask);
        window.removeEventListener("resize", updateMask);
      };
    }
  }, [overflowScroll, scrollToBottom, scrollToTop, scrollToIndex, onMounted, _onScroll]);

  let {className} = others ?? {};

  return (
    <XDiv overflow="auto" {...others} noTouchAction={false} 
      forwardRef={setRefs}
      overflowScroll={overflowScroll}
      className={masked ? "masked-overflow" : className}>
      {children}
    </XDiv>
  );
}

export default XScroll;